import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit3, 
  User, 
  Mail, 
  Calendar, 
  Hash, 
  Phone, 
  Instagram,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import ArtworkAdmin from '../ArtworkAdmin';
import '../../styles/UserDetailPage.css';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userId) {
      loadUserDetails();
    }
  }, [userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Load user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('uuid', userId)
        .single();

      if (userError) {
        throw userError;
      }

      // Load user roles
      const { data: userRoles, error: roleError } = await supabase
        .from('user_roles')
        .select(`
          role_id,
          roles (role_name)
        `)
        .eq('user_id', userId);

      if (roleError) {
        console.error('Error loading user roles:', roleError);
      }

      // Combine user data with roles
      const userWithRoles = {
        ...userData,
        roles: userRoles || []
      };

      setUser(userWithRoles);

    } catch (error) {
      console.error('Error loading user details:', error);
      setError(`Failed to load user details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (roleId) => {
    const roleNames = {
      1: 'Member',
      2: 'Collector/Patron',
      3: 'Admin',
      4: 'Artist'
    };
    return roleNames[roleId] || `Role ${roleId}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            className="btn-primary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-page">
        <div className="error-container">
          <h2>User Not Found</h2>
          <p>The requested user could not be found.</p>
          <button 
            className="btn-primary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-page">
      {/* Back Button and Edit Profile */}
      <div className="back-button-container">
        <button 
          type="button" 
          className="back-button-standalone"
          onClick={() => navigate('/admin/users')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>
        <div className="action-buttons">
          <button
            type="button"
            className="edit-button-standalone"
            onClick={() => navigate(`/admin/users/${userId}/edit`)}
          >
            <Edit3 className="w-4 h-4" />
            Edit Profile
          </button>
          {user && user.preferences && user.preferences.home === 'artist' && (
            <button
              type="button"
              className="add-artwork-button"
              onClick={() => navigate(`/admin/users/${userId}/add-artwork`)}
            >
              <i className="fas fa-plus"></i>
              Add Artwork
            </button>
          )}
        </div>
      </div>

      {/* User Profile */}
      <div className="user-profile-section">
        <div className="profile-header">
          <div className="profile-image-container">
            {user.profile_img ? (
              <img 
                src={user.profile_img} 
                alt={`${user.first_name} ${user.last_name}`}
                className="profile-image"
              />
            ) : (
              <div className="profile-image-placeholder">
                <User className="w-16 h-16" />
              </div>
            )}
          </div>
          
          <div className="profile-info">
            <h2>{user.first_name} {user.last_name}</h2>
            {user.nickname && <p className="nickname">"{user.nickname}"</p>}
            <p className="email">{user.email}</p>
            {user.handle && <p className="handle">@{user.handle}</p>}
            
            {/* Role and Commission Status */}
            <div className="profile-badges">
              {user.preferences && user.preferences.home && (
                <div className="roles-display">
                  <span className="role-badge">
                    {user.preferences.home.charAt(0).toUpperCase() + user.preferences.home.slice(1)}
                  </span>
                </div>
              )}
              <div className="commission-status">
                {user.commissions ? (
                  <span className="status-active">Accepts Commissions</span>
                ) : (
                  <span className="status-inactive">Does Not Accept Commissions</span>
                )}
              </div>
            </div>
            
            <div className="profile-stats">
              <div className="stat">
                <span className="stat-label">Member Since</span>
                <span className="stat-date">{formatDate(user.created_at)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Last Updated</span>
                <span className="stat-date">{formatDate(user.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {user.cover_image && (
          <div className="cover-image-container">
            <img 
              src={user.cover_image} 
              alt="Cover"
              className="cover-image"
            />
          </div>
        )}
      </div>

      {/* Artist Artworks Section - Only show if user is an artist */}
      {user.preferences && user.preferences.home === 'artist' && (
        <div className="artist-artworks-section">
          <div className="section-header">
            <h3>Artworks by {user.first_name} {user.last_name}</h3>
            <p>View all artworks created by this artist</p>
          </div>
          <ArtworkAdmin artistFilter={user.uuid} />
        </div>
      )}

    </div>
  );
};

export default UserDetailPage;